/**
 * @ts-check
 * @prettier
 * @author Alexander Nagelberg
 *
 **/
import { SubmissionError } from 'redux-form'
import { genericFetchingActionCreator, genericPostingActionCreator } from 'app/actions/genericActionCreator'
import { catchCommonErrors, fetchPostingParams } from 'app/actions/commonActionUtils'
import { convertLgyErrorResponseToFormErrorObject } from 'app/utils/formUtils'
import { getBrowser } from 'app/utils/browserUtils'
import { URLs, fullURL } from '../utils/refundUtils'

const downloadCSV = async ({ URL, header, dispatch }) => {
  dispatch(genericPostingActionCreator('FUNDING_FEE', 'posting'))
  let uuid = null
  try {
    const response = header ? await fetch(URL, header) : await fetch(URL)
    uuid = response.headers.get('x-lgyrequestuuid') ? response.headers.get('x-lgyrequestuuid').split(',')[0] : null
    // Special case for handling downloadable content
    if (response.status === 200 && response.headers.get('content-type') === 'text/csv') {
      const browser = getBrowser()?.name
      const blob = await response.blob()
      const filename = `${response.headers.get('content-disposition')?.match(/filename=(.*?)\.csv/)[1]}.csv`

      if (browser === 'IE') {
        // note: msSaveBlob is deprecated in MDN (but then, so is IE....)
        navigator.msSaveOrOpenBlob(blob, filename)
      } else {
        // straight up DOM hack to set filename by using download attr of anchor tag
        // TODO: in the future is there a better way?
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.rel = 'noopener noreferrer'
        anchor.download = filename
        anchor.click()
        window.URL.revokeObjectURL(url) // cleanup
      }
      dispatch(genericPostingActionCreator('FUNDING_FEE', 'posted', { success: true }))
    } else {
      const responseJson = await catchCommonErrors(dispatch, response, 'applying action to csv download')
      if (response.status >= 400) {
        console.error('error fetching CSV')
        dispatch(genericPostingActionCreator('FUNDING_FEE', 'error', responseJson))
      } else {
        dispatch(genericPostingActionCreator('FUNDING_FEE', 'error', "didn't get right mime type"))
      }
    }
  } catch (e) {
    console.error(e)
    const errors = { errors: [{ message: `Unexpected error occured. Reference id: ${uuid}` }] }
    dispatch(genericPostingActionCreator('FUNDING_FEE', 'error', errors))
  }
}

export const loadRefund = refundId => async dispatch => {
  let initialRefund = {}
  initialRefund[refundId] = null
  dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetching', initialRefund))

  try {
    const response = await fetch(fullURL(URLs.GET_REFUND_PATH, refundId), {
      method: 'GET',
      credentials: 'include'
    })
    const responseJson = await catchCommonErrors(dispatch, response, 'fetching refund')
    if (response.status >= 400) {
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'error', responseJson))
      console.error(`Error fetching refund (refund id: ${refundId})`)
    } else {
      const payload = {}
      payload[refundId] = responseJson
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetched', payload))
    }
  } catch (e) {
    // TODO: pull this? check to see if the status > 400 is enough
    dispatch(genericFetchingActionCreator('FUNDING_FEE', 'error', e))
    console.error(e)
  }
}

const prepareFilterURL = args => {
  const { lin, from, to, status, failure, firstName, lastName } = args // filter args
  const { page, limit } = args // page args

  const params = new URLSearchParams()

  if (page != null && limit != null) {
    params.set('page', page)
    params.set('limit', limit)
  }

  if (lin) {
    params.set('lin', lin)
  } else if (from && to) {
    params.set('from', from)
    params.set('to', to)
  } else if (status) {
    params.set('status', status)
  } else if (failure) {
    params.set('failure', failure)
  } else if (firstName && lastName) {
    params.set('firstName', firstName)
    params.set('lastName', lastName)
  }
  return params.toString()
}

export const updateRefundRecord = formValues => async dispatch => {
  dispatch(genericPostingActionCreator('FUNDING_FEE', 'posting'))
  try {
    const response = await fetch(fullURL(URLs.UPDATE_REFUND_PATH, formValues.id), fetchPostingParams(formValues))
    const responseJson = await catchCommonErrors(dispatch, response, 'updating refund')

    if (response.status >= 400) {
      dispatch(genericPostingActionCreator('FUNDING_FEE', 'error', responseJson))
      console.error(`Error updating refund (refund id: ${formValues.id})`)
      // TODO: is there a way to distinguish validation errors from other? big errors fail without UUID this way
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
    } else {
      const payload = {}
      payload[formValues.id] = responseJson
      dispatch(genericPostingActionCreator('FUNDING_FEE', 'posted', payload))
    }
  } catch (e) {
    if (e instanceof SubmissionError) {
      throw e
    }
    console.error(e)
  }
}

export const loadRefunds = args => async dispatch => {
  const { limit, page, merge, ...filterArgs } = args
  const { lin, from, to, status, failure, firstName, lastName } = filterArgs

  const query = prepareFilterURL(args)

  dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetching'))
  try {
    const response = await fetch(`${fullURL(URLs.SEARCH_PATH)}?${query}`)
    const responseJson = await catchCommonErrors(dispatch, response, 'fetching refunds')
    if (response.status >= 400) {
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'error', responseJson))
      console.error(
        'Error fetching refunds ' +
          (lin
            ? `with lin ${lin}`
            : from && to
            ? `from epoch ${from} to epoch ${to}`
            : status
            ? `with status ${status}`
            : failure
            ? `with failure reason ${failure}`
            : `with name ${lastName}, ${firstName}`)
      )
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
      // TODO: remove if a form is not calling this action
    } else {
      dispatch({
        type: merge ? 'MERGE_FUNDING_FEE_REFUNDS' : 'FETCHED_FUNDING_FEE',
        data: {
          refunds: responseJson.map((refund, index) => ({ ...refund, pageIndex: index })),
          filter: filterArgs,
          page: { page, limit }
        }
      })
    }
  } catch (e) {
    if (e instanceof SubmissionError) {
      throw e
    }
    console.error(e)
  }
}

export const prefetchRefunds = args => async dispatch => loadRefunds({ ...args, merge: true })(dispatch)

export const clearRefunds = () => dispatch => {
  dispatch({ type: 'CLEAR_FUNDING_FEE', data: { refunds: null } })
}

export const fetchReport = ({ action, groupName }) => dispatch => {
  const path = action === 'payment' ? 'action/payment' : action === 'prescreen' ? 'prescreen' : ''

  downloadCSV({
    URL: `${fullURL(path)}/retrieve`,
    header: fetchPostingParams({ groupName }),
    dispatch
  })
}

export const fetchReportList = action => async dispatch => {
  const path = action === 'PAYMENT' ? 'action/payment' : action === 'PRESCREEN' ? 'prescreen' : ''

  dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetching'))
  try {
    const response = await fetch(`${fullURL(path)}/list`)
    const responseJson = await catchCommonErrors(dispatch, response, 'fetching report list')
    if (response.status >= 400) {
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'error', responseJson))
    } else {
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetched', { reports: responseJson }))
    }
  } catch (e) {
    console.error(e)
  }
}

export const validateRefund = refundId => async dispatch => {
  dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetching'))
  try {
    const response = await fetch(fullURL(refundId, URLs.REFUND_VALIDATION_PARAM))
    const responseJson = await catchCommonErrors(dispatch, response, 'validating refund')
    if (response.status >= 400) {
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'error', responseJson))
    } else {
      const payload = { validationSucceeded: responseJson.succeeded }
      payload[refundId] = responseJson.refund
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetched', payload))
    }
  } catch (e) {
    console.error(e)
  }
}

export const fetchRefundTotals = args => async dispatch => {
  const query = prepareFilterURL(args)

  dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetching'))
  try {
    const response = await fetch(`${fullURL(URLs.REFUND_TOTALS_PATH)}?${query}`)
    const responseJson = await catchCommonErrors(dispatch, response, 'fetching refund totals')
    if (response.status >= 400) {
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'error', responseJson))
      console.error('Error fetching totals')
    } else {
      dispatch(genericFetchingActionCreator('FUNDING_FEE', 'fetched', { refundsInfo: responseJson }))
      loadRefunds(args)(dispatch)
    }
  } catch (e) {
    console.error(e)
  }
}

export const createNote = args => async dispatch => {
  const { refundId, ...form } = args
  dispatch(genericPostingActionCreator('FUNDING_FEE', 'posting', { newNote: null }))
  try {
    const response = await fetch(
      `${URLs.FUNDING_FEE_REFUND_PATH}/${refundId}/${URLs.NOTE_PATH}`,
      fetchPostingParams(form)
    )
    const responseJson = await catchCommonErrors(dispatch, response, 'creating refund note')
    if (response.status >= 400) {
      dispatch(genericPostingActionCreator('FUNDING_FEE', 'error', responseJson))
      console.error('Error creating refund note')
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
    } else {
      // Reloaad the refund, pass along the new note's id
      await loadRefund(refundId)(dispatch)
      dispatch(genericPostingActionCreator('FUNDING_FEE', 'posted', { newNote: responseJson, success: true }))
    }
  } catch (e) {
    if (e instanceof SubmissionError) {
      throw e
    }
    console.error(e)
  }
}

export const clearNewNote = () => dispatch => {
  dispatch({ type: 'CLEAR_FUNDING_FEE', data: { newNote: null } })
}

export const exportResults = args => dispatch => {
  const query = prepareFilterURL(args)
  downloadCSV({
    URL: `${fullURL(URLs.SEARCH_PATH)}?${query}`,
    header: {
      headers: { Accept: 'text/csv' }
    },
    dispatch
  })
}
